import { useMemo, useState } from "react";
import { Box, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent, { DataHeaderType } from "components/table/TableRowComponent";
import { HEADER_PRICE_SETTING } from "constants/data";
import TextFieldComponent from "components/input/TextFieldComponent";
import { restrictDecimalInput } from "utils";
import { BrandDetailTireEdit, BrandDetailTireUpdateRequest } from "features/proc_fee/types/tire";

interface Props {
  tireBrandList: BrandDetailTireEdit[];
  onChangeItem?: (index: number, key: string, value: any) => void;
  onUpdateItem?: (index: number, key: keyof BrandDetailTireUpdateRequest) => void;
}

/**
 * TablePriceSetting component renders a table with price settings.
 * @param {Props} props - The properties for the component.
 * @returns {Element} The rendered table component.
 */
export default function TablePriceSetting({
  tireBrandList,
  onChangeItem,
  onUpdateItem,
}: Props) {
  const [error, setError] = useState<Record<string, boolean>>({});

  // Modify header labels to add ※1 and ※2 with vertical centering
  const modifiedHeaders = useMemo(
    () =>
      HEADER_PRICE_SETTING.map((header): DataHeaderType => {
        if (header.label === "A表売価掛率") {
          return {
            ...header,
            label: (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  lineHeight: "normal",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                }}
              >
                {header.label}
                <Box component="span" sx={{ ml: 0.5, fontSize: { xs: 16, md: 18, lg: 20 } }}>
                  ※1
                </Box>
              </Box>
            ),
          };
        }
        if (header.label === "利益率（％）") {
          return {
            ...header,
            label: (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  lineHeight: "normal",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                }}
              >
                {header.label}
                <Box component="span" sx={{ ml: 0.5, fontSize: { xs: 16, md: 18, lg: 20 } }}>
                  ※2
                </Box>
              </Box>
            ),
          };
        }
        return header;
      }),
    [],
  );

  const onChangeFloatNumberCell = (index: number, key: string, value: string) => {
    const newValue = restrictDecimalInput(value, 3);
    if (onChangeItem) onChangeItem(index, key, newValue);
  };

  return (
    <TableContainer
      component={Table}
      sx={{
        boxShadow: "none",
        width: "100%",
        minWidth: 1260,
        borderCollapse: "unset",
        borderSpacing: "2px 1px",
      }}
    >
      <TableHead
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: "var(--offwhite-400)",
        }}
      >
        <TableRowComponent data={modifiedHeaders} type="header" />
      </TableHead>
      <TableBody>
        {tireBrandList.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={item.brand_cd + index}>
            <TableCellComponent
              sx={{
                backgroundColor: "var(--offwhite-400)",
                border: "2px solid var(--silver-150)",
              }}
            >
              {item.brand_cd}
            </TableCellComponent>

            <TableCellComponent
              sx={{
                backgroundColor: "var(--offwhite-400)",
                border: "2px solid var(--silver-150)",
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "regular",
                textAlign: "left",
              }}
            >
              {item.maker_name}
            </TableCellComponent>

            <TableCellComponent
              sx={{
                backgroundColor: "var(--offwhite-400)",
                border: "2px solid var(--silver-150)",
                fontWeight: "regular",
                textAlign: "left",
              }}
            >
              {item.brand_name}
            </TableCellComponent>

            <TableCellComponent
              sx={{
                backgroundColor: "var(--offwhite-400)",
                border: "2px solid var(--silver-150)",
                fontWeight: "regular",
                textAlign: "center",
                px: "4px",
              }}
            >
              {item.item_category_m || ""}
            </TableCellComponent>

            <TableCellComponent sx={{ p: 0, borderBottom: "none" }}>
              <TextFieldComponent
                type="string"
                normalizeHalfWidth
                sx={{ height: "100%", my: 0 }}
                sxRoot={{ height: "100%", alignItems: "start", mt: 0 }}
                sxBox={{ height: "100%" }}
                sxInput={{
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  color: item.sale_price_rate_atable_color,
                  textAlign: "center",
                  height: "100%",
                  padding: 0,
                  boxSizing: "border-box",
                }}
                sxNotchedOutline={{
                  border: "2px solid var(--silver-150)",
                  borderColor: error[`sale_price_rate_atable-${index}`]
                    ? "var(--red-500)"
                    : "var(--silver-150)",
                  borderRadius: 0,
                }}
                value={item.sale_price_rate_atable}
                onInputText={(e) =>
                  onChangeFloatNumberCell(index, "sale_price_rate_atable", e.target.value)
                }
                onBlur={() =>
                  onUpdateItem &&
                  !error[`sale_price_rate_atable-${index}`] &&
                  onUpdateItem(index, "sale_price_rate_atable")
                }
                onError={(e) => {
                  setError((prev) => ({
                    ...prev,
                    [`sale_price_rate_atable-${index}`]: e,
                  }));
                }}
                validateOption={{
                  numericFormat: {
                    precision: 9,
                    scale: 2,
                  },
                }}
              />
            </TableCellComponent>

            <TableCellComponent sx={{ p: 0, borderBottom: "none" }}>
              <TextFieldComponent
                type="string"
                normalizeHalfWidth
                sx={{ height: "100%", my: 0 }}
                sxRoot={{ height: "100%", alignItems: "start", mt: 0 }}
                sxBox={{ height: "100%" }}
                sxInput={{
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  color: item.fixed_discount_color,
                  textAlign: "center",
                  height: "100%",
                  padding: 0,
                  boxSizing: "border-box",
                }}
                sxNotchedOutline={{
                  border: "2px solid var(--silver-150)",
                  borderColor: error[`fixed_discount_color-${index}`]
                    ? "var(--red-500)"
                    : "var(--silver-150)",
                  borderRadius: 0,
                }}
                value={item.fixed_discount}
                onInputText={(e) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  onChangeItem &&
                    onChangeItem(index, "fixed_discount", e.target.value.replace(/[^0-9]/g, ""));
                }}
                onBlur={() =>
                  onUpdateItem &&
                  !error[`fixed_discount-${index}`] &&
                  onUpdateItem(index, "fixed_discount")
                }
                onError={(e) => {
                  setError((prev) => ({
                    ...prev,
                    [`fixed_discount-${index}`]: e,
                  }));
                }}
                validateOption={{
                  maxLength: {
                    value: 9,
                    message: "",
                  },
                }}
              />
            </TableCellComponent>

            <TableCellComponent sx={{ p: 0, borderBottom: "none" }}>
              <TextFieldComponent
                type="string"
                normalizeHalfWidth
                sx={{ height: "100%", my: 0 }}
                sxRoot={{ height: "100%", alignItems: "start", mt: 0 }}
                sxBox={{ height: "100%" }}
                sxInput={{
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  color: item.set_profit_ratio_color,
                  textAlign: "center",
                  height: "100%",
                  padding: 0,
                  boxSizing: "border-box",
                }}
                sxNotchedOutline={{
                  border: "2px solid var(--silver-150)",
                  borderColor: error[`set_profit_ratio-${index}`]
                    ? "var(--red-500)"
                    : "var(--silver-150)",
                  borderRadius: 0,
                }}
                value={item.set_profit_ratio}
                onInputText={(e) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  onChangeItem &&
                    onChangeItem(
                      index,
                      "set_profit_ratio",
                      e.target.value.replace(/[^0-9]/g, ""),
                    );
                }}
                onBlur={() =>
                  onUpdateItem &&
                  !error[`set_profit_ratio-${index}`] &&
                  onUpdateItem(index, "set_profit_ratio")
                }
                onError={(e) => {
                  setError((prev) => ({
                    ...prev,
                    [`set_profit_ratio-${index}`]: e,
                  }));
                }}
                validateOption={{
                  maxLength: {
                    value: 9,
                    message: "",
                  },
                }}
              />
            </TableCellComponent>
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
}
